/**
 * Tenant-Admin Bookings inbox — `/admin/bookings`.
 *
 * Same backend as ``MerchantBookings`` (the ``GET /api/merchant/bookings``
 * endpoint accepts TENANT_ADMIN role too — when called by a tenant admin
 * the response is scoped to every shop in the tenant). UI mirrors the
 * merchant page but adds a per-shop selector so admins can filter without
 * leaving the page.
 */
import { useEffect, useMemo, useState } from "react";
import { Calendar, Phone, MapPin, IndianRupee, Check, CheckCheck, X, Store, Navigation } from "lucide-react";
import { api, inr } from "../../lib/api";
import { toast } from "sonner";
import AdminShopPicker from "../../components/AdminShopPicker";
import BookingLocationMap from "../../components/BookingLocationMap";
import CallButton from "../../components/CallButton";
import { openMap } from "../../lib/openMap";

const STATUSES = ["ALL", "PENDING", "CONFIRMED", "COMPLETED", "CANCELLED"];
const PILL = {
    PENDING: "bg-amber-100 text-amber-800",
    CONFIRMED: "bg-emerald-100 text-emerald-800",
    COMPLETED: "bg-blue-100 text-blue-800",
    CANCELLED: "bg-red-100 text-red-700",
};

export default function AdminBookings() {
    const [rows, setRows] = useState([]);
    const [shops, setShops] = useState([]);
    const [filter, setFilter] = useState("ALL");
    const [shop, setShop] = useState("");

    const load = () => {
        const params = new URLSearchParams();
        if (filter !== "ALL") params.append("status", filter);
        if (shop) params.append("restaurant_id", shop);
        const q = params.toString();
        api.get(`/merchant/bookings${q ? `?${q}` : ""}`)
            .then(({ data }) => setRows(data || []))
            .catch(() => setRows([]));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, shop]);

    useEffect(() => {
        api.get("/admin/restaurants")
            .then(({ data }) => setShops(data || []))
            .catch(() => setShops([]));
    }, []);

    const shopName = useMemo(() => {
        const m = {};
        for (const s of shops) m[s.id] = s.name;
        return m;
    }, [shops]);

    const update = async (b, status) => {
        try {
            await api.patch(`/merchant/bookings/${b.id}/status`, { status });
            toast.success(`Marked ${status}`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    return (
        <div data-testid="admin-bookings-page">
            <h1 className="text-2xl font-bold">Service Bookings</h1>
            <p className="text-sm text-muted-foreground mt-1">{rows.length} {filter !== "ALL" ? filter.toLowerCase() : ""} booking{rows.length === 1 ? "" : "s"} across all shops</p>

            <div className="mt-4 flex flex-wrap gap-3 items-center">
                <div className="flex gap-2 overflow-x-auto no-scrollbar">
                    {STATUSES.map((s) => (
                        <button
                            key={s}
                            data-testid={`admin-bookings-filter-${s}`}
                            onClick={() => setFilter(s)}
                            className={`text-xs font-semibold px-3 py-1.5 rounded-full border whitespace-nowrap ${filter === s ? "bg-foreground text-background border-foreground" : "border-border"}`}
                        >{s}</button>
                    ))}
                </div>
                <div className="ml-auto">
                    <AdminShopPicker
                        testIdPrefix="admin-bookings-shop"
                        value={shop}
                        onChange={setShop}
                        allShopsOption
                        shopsOverride={shops}
                    />
                </div>
            </div>

            <div className="mt-5 space-y-3">
                {rows.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground"><Calendar size={28} className="inline opacity-40" /><p className="mt-2">No bookings yet.</p></div>
                ) : rows.map((b) => (
                    <article key={b.id} data-testid={`adm-booking-${b.id}`} className="bg-card border border-border rounded-2xl p-4">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <h3 className="font-bold line-clamp-1">{b.product_name}</h3>
                                <p className="text-xs text-muted-foreground inline-flex items-center gap-1"><Store size={11} /> {shopName[b.restaurant_id] || "—"} · {b.customer_name} · {b.duration_min} min</p>
                            </div>
                            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${PILL[b.status] || "bg-muted"}`}>{b.status}</span>
                        </div>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-y-1 text-sm">
                            <div className="flex items-center gap-2"><Calendar size={14} className="text-muted-foreground" /> {new Date(b.scheduled_at).toLocaleString()}</div>
                            <div className="flex items-center gap-2"><Phone size={14} className="text-muted-foreground" /> <CallButton phone={b.customer_mobile} className="text-primary">{b.customer_mobile}</CallButton></div>
                            {b.address && (
                                <div className="flex items-start gap-2 col-span-full">
                                    <MapPin size={14} className="text-muted-foreground mt-0.5" />
                                    <span className="text-muted-foreground">
                                        {/* iter83 fix — ``address`` arrives as an object
                                            ({label, line1, line2, city, state, pincode}).
                                            Pre-iter82 versions of this page rendered it
                                            directly, which crashed React with
                                            "Objects are not valid as a React child".
                                            Coerce to a single readable line. */}
                                        {typeof b.address === "string"
                                            ? b.address
                                            : [b.address.label, b.address.line1, b.address.line2, b.address.city, b.address.pincode]
                                                .filter(Boolean).join(", ")}
                                    </span>
                                </div>
                            )}
                            <div className="flex items-center gap-2 col-span-full"><IndianRupee size={14} className="text-muted-foreground" /> Advance {inr(b.advance_paid)} of {inr(b.amount_total)}</div>
                            <div className="col-span-full">
                                <BookingLocationMap
                                    lat={b.address?.lat}
                                    lng={b.address?.lng}
                                    label={b.customer_name}
                                    testid={`adm-booking-map-${b.id}`}
                                />
                                {/* Navigate only for AT_CUSTOMER — the admin/merchant is
                                    the one travelling, to the customer's address. AT_SHOP
                                    bookings store the SHOP's own address here instead (the
                                    customer travels there), so there's nothing to navigate to. */}
                                {b.location_type !== "AT_SHOP" && (b.address?.lat != null || b.address) && (
                                    <button
                                        type="button"
                                        data-testid={`adm-booking-nav-${b.id}`}
                                        onClick={() => openMap({
                                            lat: b.address?.lat, lng: b.address?.lng,
                                            label: b.customer_name,
                                            address: typeof b.address === "string" ? b.address : undefined,
                                        })}
                                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-400 rounded-full px-3 py-1.5"
                                    ><Navigation size={13} /> Navigate</button>
                                )}
                            </div>
                            {b.notes && <div className="col-span-full text-xs text-muted-foreground italic">&ldquo;{b.notes}&rdquo;</div>}
                        </div>
                        {b.status === "PENDING" && (
                            <div className="mt-3 flex gap-2">
                                <button data-testid={`adm-confirm-${b.id}`} onClick={() => update(b, "CONFIRMED")} className="bg-emerald-600 text-white rounded-xl px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1"><Check size={13} /> Confirm</button>
                                <button data-testid={`adm-cancel-${b.id}`} onClick={() => update(b, "CANCELLED")} className="border border-border rounded-xl px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1"><X size={13} /> Decline</button>
                            </div>
                        )}
                        {b.status === "CONFIRMED" && (
                            <div className="mt-3">
                                <button data-testid={`adm-complete-${b.id}`} onClick={() => update(b, "COMPLETED")} className="bg-primary text-primary-foreground rounded-xl px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1"><CheckCheck size={13} /> Mark Completed</button>
                            </div>
                        )}
                    </article>
                ))}
            </div>
        </div>
    );
}
